/**
 * pages/MyListings.tsx - Seller's Listings Management Page
 * Tabbed view: Active / Under Review / Sold / Draft / Paused / My Favorites
 * Edit and delete actions per listing
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { listingsAPI } from '@/lib/api/listings';
import { apiClient } from '@/lib/api-client';
import {
  Package,
  Loader2,
  AlertCircle,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  MessageSquare,
  RefreshCw,
  Heart,
  HeartOff,
  MapPin,
} from 'lucide-react';
import { formatCategory } from '@/lib/utils';
import SEO from '@/components/common/SEO';

type ListingStatus = 'ACTIVE' | 'PENDING_REVIEW' | 'SOLD' | 'DRAFT' | 'PAUSED';
type TabType = ListingStatus | 'FAVORITES';

interface ListingItem {
  id: string;
  title: string;
  category: string;
  price: number;
  status: ListingStatus;
  views: number;
  imageUrl?: string;
  images?: string[];
  createdAt: string;
  location?: string;
  description?: string;
}

const STATUS_CONFIG: Record<ListingStatus, { label: string; icon: typeof CheckCircle2; color: string }> = {
  ACTIVE: { label: 'Active', icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
  PENDING_REVIEW: { label: 'Under Review', icon: Clock, color: 'text-amber-600 bg-amber-50 border border-amber-200' },
  SOLD: { label: 'Sold', icon: XCircle, color: 'text-red-600 bg-red-50' },
  DRAFT: { label: 'Draft', icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
  PAUSED: { label: 'Paused', icon: AlertCircle, color: 'text-gray-600 bg-gray-50' },
};

const MyListings: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('ACTIVE');
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [sellerLoading, setSellerLoading] = useState(true);
  const [removingFavId, setRemovingFavId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Resolve seller profile to get the correct seller UUID
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;

    const fetchSeller = async () => {
      setSellerLoading(true);
      try {
        const resp = await apiClient.get(`/api/sellers/user/${user.sub}`);
        if (resp?.id) {
          setSellerId(resp.id);
        } else {
          setError('Seller profile not found. Post an ad first to create your profile.');
        }
      } catch (err: any) {
        console.error('Error fetching seller profile:', err);
        if (err?.status === 404) {
          setError('No seller profile found. Post an ad to create one automatically.');
        } else {
          setError(err?.message || 'Failed to load seller profile.');
        }
      } finally {
        setSellerLoading(false);
        setLoading(false);
      }
    };

    fetchSeller();
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (activeTab === 'FAVORITES') {
      loadFavorites();
    } else if (sellerId) {
      loadListings();
    }
  }, [activeTab, sellerId]);

  const loadListings = async () => {
    if (!sellerId) return;
    setLoading(true);
    setError(null);

    try {
      const data = await listingsAPI.getSellerListings(sellerId);
      // Filter by status for the current tab
      const all = Array.isArray(data)
        ? data
        : (data.items || data.listings || data.content || []);
      const filtered = all.filter(
        (l: any) => l.status === activeTab
      );
      setListings(filtered);
    } catch (err: any) {
      console.error('Error loading listings:', err);
      setError(err?.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listingsAPI.getLiked();
      const items = Array.isArray(data)
        ? data
        : (data?.items || data?.listings || data?.content || []);
      setFavorites(items);
    } catch (err: any) {
      console.error('Error loading favorites:', err);
      // The backend may not have a /liked endpoint yet (returns 500 because
      // the /{id} route tries to parse "liked" as a UUID). Treat server
      // errors gracefully by showing an empty list instead of an error banner.
      if (err?.status === 500 || err?.status === 404) {
        setFavorites([]);
      } else {
        setError(err?.message || 'Failed to load favorites');
        setFavorites([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (listingId: string) => {
    setRemovingFavId(listingId);
    try {
      await listingsAPI.toggleLike(listingId);
      setFavorites(prev => prev.filter(f => f.id !== listingId));
    } catch (err: any) {
      console.error('Error removing favorite:', err);
      setError(err?.message || 'Failed to remove from favorites');
    } finally {
      setRemovingFavId(null);
    }
  };

  const handleEdit = (listingId: string) => {
    navigate(`/post?edit=${listingId}`);
  };

  const handleDelete = async (listingId: string) => {
    setDeletingId(listingId);
    try {
      await listingsAPI.delete(listingId);
      // Remove from list immediately; clear confirmation state
      setListings(prev => prev.filter(l => l.id !== listingId));
      setConfirmDelete(null);
    } catch (err: any) {
      console.error('Error deleting listing:', err);
      setError(err?.message || 'Failed to delete listing');
      // Keep confirmation UI visible on error so user can retry or cancel
    } finally {
      setDeletingId(null);
    }
  };

  const handleMarkSold = async (listingId: string) => {
    try {
      await listingsAPI.updateListingStatus(listingId, 'SOLD');
      loadListings();
    } catch (err: any) {
      setError(err?.message || 'Failed to mark as sold');
    }
  };

  const STATUS_TABS: ListingStatus[] = ['ACTIVE', 'PENDING_REVIEW', 'SOLD', 'DRAFT', 'PAUSED'];

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <SEO title="My Listings" noindex />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Package className="h-8 w-8 text-primary" />
              My Listings
            </h1>
            <p className="text-muted-foreground mt-1">Manage your classifieds listings</p>
          </div>
          <button
            onClick={() => navigate('/post')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-semibold text-sm"
          >
            <RefreshCw className="h-4 w-4" />
            Post New Ad
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-xs text-destructive hover:underline">Dismiss</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
          {STATUS_TABS.map(tab => {
            const config = STATUS_CONFIG[tab];
            const Icon = config.icon;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {config.label}
              </button>
            );
          })}
          {/* Favorites Tab */}
          <button
            onClick={() => setActiveTab('FAVORITES')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              activeTab === 'FAVORITES'
                ? 'border-red-500 text-red-500'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Heart className={`h-4 w-4 ${activeTab === 'FAVORITES' ? 'fill-red-500' : ''}`} />
            My Favorites
          </button>
        </div>

        {/* Loading */}
        {(loading || sellerLoading) && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* ========== FAVORITES TAB CONTENT ========== */}
        {activeTab === 'FAVORITES' && !loading && !sellerLoading && (
          <>
            {/* Empty State */}
            {favorites.length === 0 && (
              <div className="text-center py-16">
                <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No favorites yet</h3>
                <p className="text-muted-foreground mb-6">
                  Tap the heart icon on any listing to save it here.
                </p>
                <Link
                  to="/listings"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-semibold text-sm"
                >
                  Browse Listings
                </Link>
              </div>
            )}

            {/* Favorites Grid */}
            {favorites.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {favorites.map((fav: any) => (
                  <div
                    key={fav.id}
                    className="relative rounded-2xl border border-border bg-card overflow-hidden transition hover:-translate-y-0.5 hover:shadow-lg group"
                  >
                    {/* Image */}
                    <Link to={`/listings/${fav.id}`} className="block relative aspect-[4/3] overflow-hidden bg-secondary">
                      {fav.imageUrl || fav.image || (Array.isArray(fav.images) && fav.images.length > 0 ? fav.images[0] : undefined) ? (
                        <img
                          src={fav.imageUrl || fav.image || (Array.isArray(fav.images) && fav.images.length > 0 ? fav.images[0] : '')}
                          alt={fav.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <Package className="h-10 w-10 text-muted-foreground" />
                        </div>
                      )}
                    </Link>

                    {/* Unfavorite Button */}
                    <button
                      onClick={() => handleRemoveFavorite(fav.id)}
                      disabled={removingFavId === fav.id}
                      aria-label="Remove from favorites"
                      className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-red-500 backdrop-blur shadow-sm transition hover:bg-red-50 hover:scale-110 disabled:opacity-50"
                    >
                      {removingFavId === fav.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Heart className="h-4 w-4 fill-red-500" />
                      )}
                    </button>

                    {/* Details */}
                    <Link to={`/listings/${fav.id}`} className="block p-3.5">
                      <div className="font-heading text-lg font-bold text-foreground">
                        ₹{(fav.price || 0).toLocaleString('en-IN')}
                      </div>
                      <h3 className="line-clamp-1 text-sm font-medium text-foreground">{fav.title}</h3>
                      {fav.description && (
                        <p className="line-clamp-1 text-xs text-muted-foreground mt-0.5">{fav.description}</p>
                      )}
                      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {fav.location || fav.city || 'India'}
                        </span>
                         <span>{formatCategory(fav.category)}</span>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ========== LISTING STATUS TAB CONTENT ========== */}
        {activeTab !== 'FAVORITES' && (
          <>
            {/* Empty State */}
            {!loading && !sellerLoading && listings.length === 0 && (
              <div className="text-center py-16">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No {STATUS_CONFIG[activeTab].label.toLowerCase()} listings</h3>
                <p className="text-muted-foreground mb-6">You haven't posted any listings in this category yet.</p>
                {activeTab === 'ACTIVE' && (
                  <button
                    onClick={() => navigate('/post')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-semibold text-sm"
                  >
                    Post Your First Ad
                  </button>
                )}
              </div>
            )}

            {/* Listing Cards */}
            {!loading && listings.length > 0 && (
              <div className="space-y-4">
                {listings.map(listing => {
                  const config = STATUS_CONFIG[listing.status];
                  const StatusIcon = config.icon;
                  return (
                    <div key={listing.id} className="rounded-xl border border-border bg-card p-4 sm:p-6">
                      <div className="flex items-start gap-4">
                        {/* Thumbnail */}
                        {listing.imageUrl || (Array.isArray(listing.images) && listing.images.length > 0 ? listing.images[0] : undefined) ? (
                          <img
                            src={listing.imageUrl || (Array.isArray(listing.images) && listing.images.length > 0 ? listing.images[0] : '')}
                            alt={listing.title}
                            className="w-20 h-20 rounded-lg object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                            <Package className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-foreground truncate">{listing.title}</h3>
                               <p className="text-sm text-muted-foreground">{formatCategory(listing.category)}</p>
                              <p className="text-lg font-bold text-primary mt-1">
                                ₹{listing.price.toLocaleString('en-IN')}
                              </p>
                            </div>
                            <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {config.label}
                            </span>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Eye className="h-3.5 w-3.5" />
                              {listing.views} views
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-3.5 w-3.5" />
                              Inquiries
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 shrink-0">
                          {listing.status === 'ACTIVE' && (
                            <button
                              onClick={() => handleMarkSold(listing.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-secondary transition"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Mark Sold
                            </button>
                          )}
                          {listing.status !== 'DRAFT' && (
                            <button
                              onClick={() => handleEdit(listing.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-secondary transition"
                            >
                              <Edit className="h-3.5 w-3.5" />
                              Edit
                            </button>
                          )}
                          {confirmDelete === listing.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(listing.id)}
                                disabled={deletingId === listing.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition disabled:opacity-50"
                              >
                                {deletingId === listing.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-secondary transition"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDelete(listing.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-destructive/30 text-xs font-medium text-red-600 hover:bg-red-50 transition"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyListings;